// Overview.jsx
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';


import '../../../../Stylesheets/Dashboard/Overview.css';

import NoProjectsView from '../../NoProjectsView';
import Metrics from './Metrics';
import ProjectCards from './ProjectCards';
import SmartSuggestions from './SmartSuggestions';
import AnalyticsPreview from './AnalyticsPreview';
import RecentActivity from './RecentActivity';
import Spinner from '../../../../Components/Common/Spinner';

import ComingSoon from '../../../../Components/Common/ComingSoon';

import {
  fetchOverview,
  selectOverview,
  selectOverviewStatus,
  selectOverviewError,
} from '../../../../Redux/Projects/overviewSlice';

// helpers
const greetingForNow = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
};
const displayNameFromUser = (u) =>
  u?.name || u?.fullName || u?.displayName || (u?.email ? u.email.split('@')[0] : 'there');

const formatLastActive = (u) => {
  const iso = u?.updatedAt || u?.lastLoginAt || u?.lastSignInTime;
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};







const Overview = () => {
  const dispatch = useDispatch();

  // overview slice
  const overviewData = useSelector(selectOverview);
  const overviewStatus = useSelector(selectOverviewStatus);
  const overviewError = useSelector(selectOverviewError);

  // user from auth slice (since /overview doesn’t include user)
  const user = useSelector((s) => s.auth.user);

  const greeting = useMemo(greetingForNow, []);
  const userName = useMemo(() => displayNameFromUser(user), [user]);
  const lastActive = useMemo(() => formatLastActive(user), [user]);

  useEffect(() => {
    if (overviewStatus === 'idle') {
      dispatch(fetchOverview({ limitProjects: 4, limitActivity: 10 }));
    }
  }, [dispatch, overviewStatus]);

  // ✅ projects is an ARRAY on data.projects (per your payload)
  const projects = Array.isArray(overviewData?.projects) ? overviewData.projects : [];
  const hasProjects = projects.length > 0;

  return (
    <div className="overview-container">
      {overviewStatus === 'loading' && (
        <div className="overview-loader">
          <Spinner size={56} label="Loading overview…" />
        </div>
      )}

      {overviewStatus === 'failed' && (
        <div className="overview-error">
          <div className="err-box">
            <div className="err-title">Couldn’t load your overview</div>
            <div className="err-msg">{String(overviewError || 'Unknown error')}</div>
            <button
              className="btn primary"
              onClick={() => dispatch(fetchOverview({ limitProjects: 4, limitActivity: 10 }))}
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {overviewStatus !== 'failed' && (
        <>
          {hasProjects ? (
            <>
              <div className="overview-header">
                <div className="welcome-info">
                  <h2>
                    {greeting}, {userName} <span className="emoji">👷‍♂️</span>
                  </h2>
                  <p className="welcome-back">Welcome back 👋</p>
                  {lastActive ? <p>Last active: {lastActive}</p> : <p>Let’s get things done.</p>}
                </div>
                <Link to="/dashboard/create">
                  <button className="create-project-btn">+ Create New Project</button>
                </Link>
              </div>

              <div className="overview-grid">
                <div className="left-panel">
                  <Metrics projects={projects} metrics={overviewData?.metrics} />
                  <div className="project-suggestion-row">
                    <div className="project-cards-section">
                      <ProjectCards projects={projects.slice(0, 2)} />
                    </div>
                    <div className="smart-suggestion-section">
                      {/* 🔒 Phase 2: Smart Suggestions */}
                      <ComingSoon
                        tone="indigo"
                        label="Smart Suggestions"
                        subLabel="AI insights and next actions will land here"
                        actionLabel="AI preview locked"
                      >
                        <SmartSuggestions suggestions={overviewData?.recentActivity || []} />
                      </ComingSoon>
                    </div>
                  </div>
                </div>
                <div className="right-panel">
                  {/* 🔒 Phase 2: Analytics */}
                  <ComingSoon label="Analytics — Coming soon">
                    <AnalyticsPreview recentActivity={overviewData?.recentActivity || []} />
                  </ComingSoon>
                </div>
              </div>


              <RecentActivity items={overviewData?.recentActivity || []} projects={projects} />

            </>
          ) : (
            overviewStatus === 'succeeded' && <NoProjectsView />
          )}
        </>
      )}
    </div>
  );
};

export default Overview;
